import {useState} from "react"

import {api} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"

// Mirrors the server allow-list (common/modules/customernotes/upload_types.go),
// which is what actually enforces it.
export const ALLOWED_UPLOAD_EXTENSIONS = [
	".pdf",
	".jpg",
	".jpeg",
	".png",
	".gif",
	".webp",
	".heic",
	".tif",
	".tiff",
	".txt",
	".csv",
	".doc",
	".docx",
	".xls",
	".xlsx",
	".eml",
	".msg",
]

export function isAllowedUploadFile(fileName: string): boolean {
	const dot = fileName.lastIndexOf(".")
	return dot !== -1 && ALLOWED_UPLOAD_EXTENSIONS.includes(fileName.slice(dot).toLowerCase())
}

export type FileUpload = {
	uploadID: string
	file: File
	uploading: boolean
	uploaded: boolean
	error?: string
}

export function useFileUpload(customerID: string) {
	const {notification} = useNotification()
	const [fileUploads, setFileUploads] = useState<FileUpload[]>([])

	const updateFileUpload = (file: File, updates: Partial<FileUpload>) => {
		setFileUploads((prev) => {
			const updated = [...prev]
			const index = updated.findIndex((u) => u.file === file)
			if (index !== -1) {
				updated[index] = {...updated[index], ...updates}
			}
			return updated
		})
	}

	const uploadFile = async (file: File): Promise<string | null> => {
		const newUpload: FileUpload = {
			uploadID: "",
			file,
			uploading: true,
			uploaded: false,
		}

		if (!isAllowedUploadFile(file.name)) {
			notification.error(`${file.name} is not an allowed file type`)
			return null
		}

		setFileUploads((prev) => [...prev, newUpload])

		try {
			// Get signed URL
			const {UploadID, SignedURL, ContentType} = await api.customer.getUploadURL(customerID, {
				FileName: file.name,
			})

			// Update upload ID
			updateFileUpload(file, {uploadID: UploadID})

			// Upload file
			await new Promise<void>((resolve, reject) => {
				const xhr = new XMLHttpRequest()
				xhr.open("PUT", SignedURL)
				xhr.setRequestHeader("Content-Type", ContentType)

				xhr.onload = () => {
					if (xhr.status === 200) {
						resolve()
					} else {
						reject(new Error("Failed to upload file"))
					}
				}

				xhr.onerror = () => reject(new Error("Failed to upload file"))

				xhr.send(file)
			})

			// Mark as uploaded
			updateFileUpload(file, {uploading: false, uploaded: true})

			return UploadID
		} catch (error: any) {
			updateFileUpload(file, {uploading: false, error: "Upload failed"})
			notification.error(
				error?.Message ? `Failed to upload ${file.name}: ${error.Message}` : `Failed to upload ${file.name}`,
			)
			return null
		}
	}

	const uploadFiles = async (files: File[]): Promise<void> => {
		for (const file of files) {
			await uploadFile(file)
		}
	}

	const removeFile = (file: File) => {
		setFileUploads((prev) => prev.filter((u) => u.file !== file))
	}

	const clearFiles = () => {
		setFileUploads([])
	}

	const isUploading = fileUploads.some((u) => u.uploading)
	const uploadedFiles = fileUploads.filter((u) => u.uploaded)

	return {
		fileUploads,
		uploadFiles,
		removeFile,
		clearFiles,
		isUploading,
		uploadedFiles,
	}
}
